// client/src/pages/seo/RestaurantQrCodeTableTentEn.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "A QR code table tent should be practical before it is decorative",
  },
  {
    id: "why",
    label: "Why are QR code table tents useful for restaurants?",
  },
  {
    id: "formats",
    label: "Which formats can a QR code table tent use?",
  },
  {
    id: "design",
    label: "What should be written on a QR code table tent?",
  },
  {
    id: "placement",
    label: "Where should a QR code table tent be placed?",
  },
  {
    id: "mistakes",
    label: "What mistakes happen with QR code table tents?",
  },
  {
    id: "recommendation",
    label: "How would I start as a restaurant owner?",
  },
  {
    id: "next-steps",
    label: "What should you do now?",
  },
  {
    id: "bestplace",
    label: "How Best Place helps you prepare QR code table tents",
  },
  {
    id: "faq",
    label: "FAQ about QR code table tents",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function RestaurantQrCodeTableTentEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Restaurant QR Code Table Tent: Place Your Digital Menu at the Table | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/restaurant-qr-code-table-tent",
          de: "/de/restaurant-qr-code-tischaufsteller",
          no: "/no/restaurant-qr-kode-bordskilt",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Restaurant QR code table tent: place your digital menu correctly
              at the table
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                A QR code table tent is often the most visible entry point to a
                digital menu. Guests see it at the table, scan the code and open
                the menu on their smartphone.
              </p>

              <p className="seoHero__text">
                But a good-looking table tent is not enough. The QR code must be
                large enough, easy to see, clearly explained and connected to a
                mobile menu that is actually up to date.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            A QR code table tent should be practical before it is decorative
          </h2>

          <p>
            Many restaurants think first about design: color, logo, material and
            shape. Those details matter, but they are not the first priority.
            The first priority is whether the guest can see, understand and scan
            the QR code quickly.
          </p>

          <p>
            A table tent should work in the real restaurant environment: with
            real lighting, real table positions, real smartphones and real
            guests who may not know what will happen after scanning.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Weaker</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">QR code is too small.</li>
                <li className="text__seo-li">
                  Table tent stands in a bad position.
                </li>
                <li className="text__seo-li">
                  Material reflects light strongly.
                </li>
                <li className="text__seo-li">Text says only “Scan me”.</li>
                <li className="text__seo-li">
                  QR code opens a hard-to-read PDF.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Better</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  QR code is large and high-contrast.
                </li>
                <li className="text__seo-li">
                  Table tent is clearly visible.
                </li>
                <li className="text__seo-li">
                  Material is tested under restaurant lighting.
                </li>
                <li className="text__seo-li">Text explains the benefit.</li>
                <li className="text__seo-li">
                  QR code opens a mobile menu page.
                </li>
              </ul>
            </div>
          </div>

          <h3>Test the menu first, then produce table tents</h3>

          <p>
            Before ordering many table tents, the digital menu should work on a
            smartphone and the QR code should be tested at real tables. This
            helps avoid printing beautiful materials that do not work well in
            daily service.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why are QR code table tents useful for restaurants?</h2>

            <p>
              A table tent is more visible than a small sticker and more
              flexible than a fixed sign. It can stand on tables, counters,
              outdoor tables, waiting areas or near the payment point.
            </p>

            <p>
              It is especially useful when the menu changes regularly: daily
              specials, drinks, seasonal offers, sold-out items or price changes
              can be updated online without reprinting the QR code.
            </p>

            <h3>The table tent is a small service point at the table</h3>

            <p>
              The table tent is not just a print product. It is a visible entry
              point into the restaurant’s digital menu. If it is clear, stable
              and easy to scan, it can help guests open the menu faster and
              reduce repeated questions for the staff.
            </p>

            <p>
              The quality is decided not only by the table tent itself, but by
              the full flow: table placement, QR code size, explanatory text,
              mobile menu layout and updated menu content.
            </p>
          </div>
        </section>

        <section id="formats" className="acquaintance">
          <h2>Which formats can a QR code table tent use?</h2>

          <p>
            Restaurants can choose between simple and more professional formats.
            For a first test, the most expensive solution is not always needed.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Laminated card.</li>
            <li className="text__seo-li">Acrylic holder.</li>
            <li className="text__seo-li">Paper card holder.</li>
            <li className="text__seo-li">Wood or metal holder.</li>
            <li className="text__seo-li">3D-printed holder.</li>
            <li className="text__seo-li">Branded table tent.</li>
            <li className="text__seo-li">Double-sided tent.</li>
            <li className="text__seo-li">Combined with daily offer.</li>
          </ul>

          <p>
            For the first step, a replaceable card is often better than a fixed
            sticker. If text, design or position does not work, the card can be
            changed quickly.
          </p>

          <VisualIdea>
            Three options side by side: laminated card, acrylic holder and a
            3D-printed holder with a replaceable QR code card.
          </VisualIdea>
        </section>

        <section id="design" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should be written on a QR code table tent?</h2>

            <p>
              A good table tent answers one question immediately: what happens
              if I scan this QR code?
            </p>

            <p>
              That is why the QR code should be supported by a short and clear
              message. The guest should not have to guess whether the code opens
              a menu, order page, payment page or review form.
            </p>

            <div className="seoCompareSmall">
              <div>
                <strong>Unclear</strong>
                <p>Scan me</p>
              </div>

              <div>
                <strong>Better</strong>
                <p>Open the digital menu</p>
              </div>
            </div>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Short headline.</li>
              <li className="text__seo-li">Large QR code.</li>
              <li className="text__seo-li">Clear call to action.</li>
              <li className="text__seo-li">Restaurant logo.</li>
              <li className="text__seo-li">Calm background.</li>
              <li className="text__seo-li">Enough contrast.</li>
              <li className="text__seo-li">Not overloaded.</li>
              <li className="text__seo-li">Optional language hint.</li>
            </ul>

            <h3>Size and scanning distance matter</h3>

            <p>
              For table tents, it is better to leave enough visual margin around
              the QR code instead of planning at the absolute minimum. Guests
              should be able to scan the code from a natural seated position,
              without moving the card too much.
            </p>
          </div>
        </section>

        <section id="placement" className="acquaintance">
          <h2>Where should a QR code table tent be placed?</h2>

          <p>
            Placement often decides whether guests use the QR code. If the table
            tent stands behind napkins, next to a vase or in shadow, guests may
            not notice it.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Make it visible:</strong> Do not hide it between
              decoration, cutlery or condiments.
            </li>

            <li className="text__seo-li">
              <strong>Avoid placing it too low:</strong> The QR code should be
              easy to scan with a smartphone.
            </li>

            <li className="text__seo-li">
              <strong>Check reflections:</strong> Lights, windows and candles
              can make scanning harder.
            </li>

            <li className="text__seo-li">
              <strong>Think double-sided:</strong> For larger tables, a
              double-sided table tent can be useful.
            </li>

            <li className="text__seo-li">
              <strong>Test in real conditions:</strong> Not only in the office,
              but at an actual table in the restaurant.
            </li>
          </ol>

          <p>
            A table tent should not feel like advertising that gets in the way.
            It should feel like a natural part of the table service.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What mistakes happen with QR code table tents?</h2>

            <p>
              Many problems happen when the table tent is planned only as a
              print item, not as part of the guest experience.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    QR code is printed before the menu is tested.
                  </li>
                  <li className="text__seo-li">
                    Code is too small or too heavily styled.
                  </li>
                  <li className="text__seo-li">
                    Glossy material reflects restaurant lighting.
                  </li>
                  <li className="text__seo-li">
                    Table tent stands in a poor position.
                  </li>
                  <li className="text__seo-li">
                    Text does not explain the purpose.
                  </li>
                  <li className="text__seo-li">
                    QR code opens an old PDF.
                  </li>
                  <li className="text__seo-li">
                    No replacement cards for damaged table tents.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Test the digital menu first.
                  </li>
                  <li className="text__seo-li">
                    Print a large, high-contrast QR code.
                  </li>
                  <li className="text__seo-li">
                    Check material under real lighting.
                  </li>
                  <li className="text__seo-li">
                    Test placement at the table.
                  </li>
                  <li className="text__seo-li">
                    Write clearly: “Open the digital menu”.
                  </li>
                  <li className="text__seo-li">Use a stable menu link.</li>
                  <li className="text__seo-li">
                    Keep some replacement cards ready.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>How would I start as a restaurant owner?</h2>

          <p>
            I would not immediately order a large quantity of professional table
            tents. I would start with a small test.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Prepare the digital menu:</strong> Check the menu on a
              smartphone.
            </li>

            <li className="text__seo-li">
              <strong>Test the QR code:</strong> Open it with iPhone and
              Android.
            </li>

            <li className="text__seo-li">
              <strong>Create 2 to 3 test table tents:</strong> Simple, but easy
              to read.
            </li>

            <li className="text__seo-li">
              <strong>Place them in the restaurant:</strong> At real tables and
              under real lighting.
            </li>

            <li className="text__seo-li">
              <strong>Ask the staff:</strong> What questions do guests ask?
            </li>

            <li className="text__seo-li">
              <strong>Produce final version later:</strong> Order a larger
              quantity only after the test.
            </li>
          </ol>

          <p>
            This reduces the risk of having beautiful table tents that do not
            work well in daily restaurant use.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should you do now?</h2>

            <p>
              If you want to create QR code table tents for your restaurant,
              start with a simple practical test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                Create a digital menu first.
              </li>
              <li className="text__seo-li">
                Open the menu on a smartphone.
              </li>
              <li className="text__seo-li">
                Create a QR code with a stable link.
              </li>
              <li className="text__seo-li">
                Print 2 to 3 test cards.
              </li>
              <li className="text__seo-li">
                Place them at real tables.
              </li>
              <li className="text__seo-li">
                Check lighting, scanning and clarity.
              </li>
              <li className="text__seo-li">
                Order the final version only after the test.
              </li>
            </ul>

            <VisualIdea>
              A small table tent test: one mobile menu, one stable QR code, two
              or three printed cards and real table placement before producing
              the final version.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ about QR code table tents</h2>

          <div className="seoFaq">
            <details>
              <summary>What is a QR code table tent?</summary>
              <p>
                It is a small card or holder placed on the restaurant table that
                lets guests open the digital menu by scanning a QR code.
              </p>
            </details>

            <details>
              <summary>What should a table tent include?</summary>
              <p>
                A large QR code, a clear message such as “Open the digital menu”
                and optionally a logo or language hint.
              </p>
            </details>

            <details>
              <summary>
                Do I need to reprint the QR code when the menu changes?
              </summary>
              <p>
                No, if the QR code points to a stable menu link. The menu
                content can be updated online.
              </p>
            </details>

            <details>
              <summary>Which material is best?</summary>
              <p>
                For testing, laminated cards or simple holders are enough. For
                long-term use, stable and easy-to-clean materials are better.
              </p>
            </details>

            <details>
              <summary>Is a sticker better than a table tent?</summary>
              <p>
                For testing, a table tent is more flexible because it can be
                moved, replaced or redesigned more easily.
              </p>
            </details>

            <details>
              <summary>Can I make QR code table tents myself?</summary>
              <p>
                Yes. You can print cards, laminate them or place them into
                simple holders. The main point is that the QR code remains easy
                to scan.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="qr"
          exclude="/en/restaurant-qr-code-table-tent"
        />
      </article>
    </main>
  );
}